import React from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { UnitCategoryCode, Risk, FundType, Umbrella, PurchasableFund, FlamingoFund, Customer } from 'funds-model';
import { AutoTestIdPrefix } from '../utils/testAutomation.utils';
import VisibilityWrapper from '../VisibilityWrapper/VisibilityWrapper';
import FundListItem from './FundListItem';
import { ROI } from './fundListOptions';
import { filterInitialState, useFilterState } from './useFilterState';
import { useSortState } from './useSortState';
import FundListFilter from './FundListFilter';
import { RenderWhenVisible, Title, SortPanel, useSelect } from '../..';
import { useFundListFilter } from './useFundListFilter';
import { createFundListSortConfig, SortConfig } from './fundListSortConfig';
import Skeleton from '../Skeleton/Skeleton';
import Decimal from 'decimal.js';
import { EmptyFiltersCard } from '../EmptyFiltersCard/EmptyFiltersCard';

import useStyles from './FundList.style';


export type Actions = {
  [index: string] : (payload?: any) => void
};

export interface FullFundListProps {
  funds?: PurchasableFund[];
  customer?: Customer;
  unitCategoryCode?: UnitCategoryCode;
  showInadequacy: boolean;
  purchaseProcessLayout?: boolean;
  hasMobilePricingDate?: boolean;
  isTestAutomationEnabled?: boolean;
  generateAutoTestId?: (prefix: string, label: string) => string | undefined;
  onSelectFund?: (fund: PurchasableFund, setIsButtonLoading?: (value: boolean) => void) => void;
  isFundVisibleForUnitCategory: (fund: PurchasableFund, unitCategory: UnitCategoryCode) => boolean;
  isFundAdequateForCustomer?: (fund: PurchasableFund, customer: Customer) => boolean;
  FundDetailsDialog?: React.ComponentType<{ fundId: number; onClose: () => void }>;
  EmptyFiltersInfoCard?: React.ComponentType<{ onClick: () => void }>;
  fundsState: {
    risks: Risk[];
    fundTypes: FundType[];
    umbrellas: Umbrella[];
  };
  initialFilterFundType?: string;
  selectedFundIds?: number[];
  disabled?: boolean;
  renderTitle?: () => void;
  renderDescription?: (unitCategoryCode: UnitCategoryCode | undefined) => void;
  dynamicUnitCategoryCodeInDetails?: boolean;
  fundDetailsDialogProcessLayout?: boolean;
  renderFundChart: (
    fund: FlamingoFund,
    unitCategoryCode: UnitCategoryCode,
    rateKey: ROI,
    options: {
      isMobile?: boolean;
      fundBoxClassName?: string;
      handleSelectMobileRateKey?: (event: React.ChangeEvent<{ value: unknown }>) => void;
    }
  ) => React.ReactNode;

  renderFundDetailsDialog?: (
    fund: FlamingoFund,
    unitCategoryCode: UnitCategoryCode,
    index: number,
    options: {
      isMobile?: boolean;
      isSelected?: boolean;
      isButtonDisabled?: boolean;
      isFundInadequate?: boolean;
      roi: ROI;
      fundDetailsDialogProcessLayout?: boolean;
      dynamicUnitCategoryCodeInDetails?: boolean;
      onSelectFund?: () => void;
      className?: string;
      mobileRateKey?: ROI;
      customButton?: React.ReactNode;
      titleAsButton?: boolean;
    }
  ) => React.ReactNode;
  renderFundTypeDropdown: (
    filterConfig: any,
    options: {
      className?: string;
      t: (key: string) => string;
    }
  ) => JSX.Element;

  renderAutocomplete: (
    filterConfig: any,
    options: {
      className?: string;
      hasUnitCategorySelect?: boolean;
      t: (key: string) => string;
    }
  ) => JSX.Element;

  renderRiskDropdown: (
    filterConfig: any,
    options: {
      className?: string;
      t: (key: string) => string;
    }
  ) => JSX.Element;

  renderUmbrellaDropdown: (
    filterConfig: any,
    options: {
      className?: string;
      t: (key: string) => string;
    }
  ) => JSX.Element;

  renderHorizonDropdown: (
    filterConfig: any,
    options: {
      className?: string;
      t: (key: string) => string;
    }
  ) => JSX.Element;
}

const FullFundList: React.FC<FullFundListProps> = ({
  funds,
  customer,
  showInadequacy,
  unitCategoryCode,
  isTestAutomationEnabled = false,
  onSelectFund,
  children,
  purchaseProcessLayout = false,
  hasMobilePricingDate = false,
  initialFilterFundType,
  selectedFundIds,
  disabled = false,
  dynamicUnitCategoryCodeInDetails,
  renderTitle,
  renderDescription,
  fundDetailsDialogProcessLayout = false,
  // New props
  isFundVisibleForUnitCategory,
  isFundAdequateForCustomer,
  FundDetailsDialog,
  EmptyFiltersInfoCard,
  fundsState,
  // Custom render functions
  renderFundChart,
  renderFundDetailsDialog,
  // Filter render functions
  renderFundTypeDropdown,
  renderAutocomplete,
  renderRiskDropdown,
  renderUmbrellaDropdown,
  renderHorizonDropdown,
  generateAutoTestId
}) => {
  const [rateKey, handleSelectRateKey] = useSelect(ROI.M12);
  const [t] = useTranslation('components');
  const { filters, actions, getFilteredFunds } = useFilterState(initialFilterFundType ? {...filterInitialState, fundType: [initialFilterFundType]} : undefined);
  const { sortConfig, requestSort, getSortedFunds } = useSortState();

  const currentUnitCategoryCode = unitCategoryCode || filters.unitCategoryCode;
  const hasUnitCategorySelect = !unitCategoryCode;
  const hasFundsInWallet = !!funds?.some(
    (fund: PurchasableFund) => fund.amountOwned,
  );

  const c = useStyles({
    hasUnitCategorySelect,
    withActionButton: !!onSelectFund
  });

  const sortedFunds = getSortedFunds(funds, sortConfig, rateKey, currentUnitCategoryCode);
  const fundsWithValues = sortedFunds
      ? sortedFunds.filter(
          fund => isFundVisibleForUnitCategory(fund, currentUnitCategoryCode)
                  && Object.keys(fund.afsCodeForUnitCode).length > 0
                  && fund.afsCodeForUnitCode[currentUnitCategoryCode] != null)
      : undefined;
  const visibleFunds = getFilteredFunds(fundsWithValues, filters, true, customer?.mifid?.targetMarketCode);

  const filterConfig = useFundListFilter(actions, filters, getFilteredFunds(fundsWithValues, filters, false, customer?.mifid?.targetMarketCode), fundsWithValues, initialFilterFundType, fundsState);

  const fundListConfig = {
    funds: visibleFunds,
    customer,
    showInadequacy,
    unitCategoryCode: currentUnitCategoryCode,
    onSelectFund,
    rateKey,
    hasMobilePricingDate,
    selectedFundIds,
    disabled,
    dynamicUnitCategoryCodeInDetails,
    fundDetailsDialogProcessLayout,
    isFundVisibleForUnitCategory,
    isFundAdequateForCustomer,
    FundDetailsDialog,
    EmptyFiltersInfoCard,
    fundsState,
    renderFundChart,
    renderFundDetailsDialog,
    renderFundTypeDropdown,
    renderAutocomplete,
    renderRiskDropdown,
    renderUmbrellaDropdown,
    renderHorizonDropdown,
    generateAutoTestId
  };
  return (
    <div className={c.root}>
      {renderTitle && (
        <div className={c.heading}>
          <Title
            titleVariant="h1"
            titleComponent="h1"
            titleFontWeight="extraBold"
            title={renderTitle()}
            description={renderDescription?.(currentUnitCategoryCode)}
            descriptionColor="greys.60"
            descriptionVariant="h4"
            titleClassName={c.title}
            titleTestid="FundListTitle"
            descriptionTestid="FundListDescription"
          />
        </div>
      )}
      <FundListFilter
        hasUnitCategorySelect={hasUnitCategorySelect}
        resultsNumber={visibleFunds ? visibleFunds.length : 0}
        actions={actions as Actions}
        hasAdequacyWithTargetMarketSelect={
          showInadequacy && customer?.mifid?.targetMarketCode != null
        }
        showWalletCheckbox={hasFundsInWallet}
        filterConfig={filterConfig}
        renderFundTypeDropdown={renderFundTypeDropdown}
        renderAutocomplete={renderAutocomplete}
        renderRiskDropdown={renderRiskDropdown}
        renderUmbrellaDropdown={renderUmbrellaDropdown}
        renderHorizonDropdown={renderHorizonDropdown}
        isTestAutomationEnabled={isTestAutomationEnabled}
      >
        {children}
      </FundListFilter>
      {visibleFunds && visibleFunds.length > 0 && (
        <SortPanel
          isTestAutomationEnabled={isTestAutomationEnabled}
          config={createFundListSortConfig(
            t,
            requestSort,
            sortConfig,
            rateKey,
            handleSelectRateKey,
            c,
            purchaseProcessLayout,
            generateAutoTestId?.(AutoTestIdPrefix.BUTTON, 'fund-list.sort-panel.roi.column-name'),
          )}
        />
      )}
      {purchaseProcessLayout ? (
        <FundList
          {...fundListConfig}
          purchaseProcessLayout={true}
          clearFiltersHandler={filterConfig.clearFilters}
        />
      ) : (
        <FundList
          {...fundListConfig}
          clearFiltersHandler={filterConfig.clearFilters}
        />
      )}
    </div>
  );
};

export interface StandardSortPanelModel {
  hasUnitCategorySelect?: boolean,
  withActionButton?: boolean,
  requestSort: (name: string) => void,
  sortConfig: SortConfig,
  rateKey: string,
  purchaseProcessLayout?: boolean,
  handleSelectRateKey: (event: React.ChangeEvent<{ value: unknown }>) => void,
  isTestAutomationEnabled?: boolean,
  generateAutoTestId?: (prefix: string, label: string) => string | undefined,
  className?: string,
}

export const StandardSortPanel: React.FC<StandardSortPanelModel> = ({
                                                                 hasUnitCategorySelect,
                                                                 withActionButton,
                                                                 requestSort,
                                                                 sortConfig,
                                                                 rateKey,
                                                                 purchaseProcessLayout = false,
                                                                 handleSelectRateKey,
                                                                 isTestAutomationEnabled = false,
                                                                 generateAutoTestId,
                                                                 className,
                                                               }) => {
  const c = useStyles({
    hasUnitCategorySelect,
    withActionButton,
  });
  const [t] = useTranslation('components');
  return <SortPanel
      config={createFundListSortConfig(
          t,
          requestSort,
          sortConfig,
          rateKey,
          handleSelectRateKey,
          c,
          purchaseProcessLayout,
          generateAutoTestId?.(AutoTestIdPrefix.BUTTON, 'fund-list.sort-panel.roi.column-name'),
      )}
      className={className}
      isTestAutomationEnabled={isTestAutomationEnabled}
  />
}

export interface FundListProps {
  funds?: PurchasableFund[];
  customer?: Customer;
  unitCategoryCode?: UnitCategoryCode;
  showInadequacy: boolean;
  purchaseProcessLayout?: boolean;
  hasMobilePricingDate?: boolean;
  generateAutoTestId?: (prefix: string, label: string) => string | undefined;
  onSelectFund?: (fund: PurchasableFund, setIsButtonLoading?: (value: boolean) => void) => void;
  isFundAdequateForCustomer?: (fund: PurchasableFund, customer: Customer) => boolean;
  EmptyFiltersInfoCard?: React.ComponentType<{ onClick: () => void }>;
  selectedFundIds?: number[];
  disabled?: boolean;
  dynamicUnitCategoryCodeInDetails?: boolean;
  fundDetailsDialogProcessLayout?: boolean;
  renderFundChart: (
      fund: FlamingoFund,
      unitCategoryCode: UnitCategoryCode,
      rateKey: ROI,
      options: {
        isMobile?: boolean;
        fundBoxClassName?: string;
        handleSelectMobileRateKey?: (event: React.ChangeEvent<{ value: unknown }>) => void;
      }
  ) => React.ReactNode;
  renderFundDetailsDialog?: (
      fund: FlamingoFund,
      unitCategoryCode: UnitCategoryCode,
      index: number,
      options: {
        isMobile?: boolean;
        isSelected?: boolean;
        isButtonDisabled?: boolean;
        isFundInadequate?: boolean;
        roi: ROI;
        fundDetailsDialogProcessLayout?: boolean;
        dynamicUnitCategoryCodeInDetails?: boolean;
        onSelectFund?: () => void;
        className?: string;
        mobileRateKey?: ROI;
        customButton?: React.ReactNode;
        titleAsButton?: boolean;
        isFundSelected?: boolean;
      }
  ) => React.ReactNode
}

export const FundList: React.FC<FundListProps & {
  rateKey: ROI,
  className?: string,
  unitCategoryCode: UnitCategoryCode,
  showNoResultsText?: boolean,
  clearFiltersHandler: () => void,
  onFundVisibility?: (fund: PurchasableFund, index: number) => void
}> =
    ({
       funds,
       customer,
       rateKey,
       unitCategoryCode,
       showInadequacy,
       onSelectFund,
       purchaseProcessLayout = false,
       hasMobilePricingDate,
       showNoResultsText = true,
       selectedFundIds,
       disabled = false,
       dynamicUnitCategoryCodeInDetails,
       className,
       fundDetailsDialogProcessLayout = false,
       clearFiltersHandler,
       isFundAdequateForCustomer,
       EmptyFiltersInfoCard,
       renderFundChart,
       renderFundDetailsDialog,
       onFundVisibility,
       generateAutoTestId,
     }) => {
      const c = useStyles({ });

      const EmptyComponent = showNoResultsText
        ? EmptyFiltersInfoCard || EmptyFiltersCard
        : null;

      const EmptyComponentRenderer = EmptyComponent ? <EmptyComponent onClick={clearFiltersHandler}/> : null

      return (
          <div className={clsx(c.list, className)}>
            {typeof funds !== 'undefined'
            ? (funds.length > 0
                ? funds.map((fund: PurchasableFund, i: number) => {
                  const isInadequate = showInadequacy && isFundAdequateForCustomer && customer ? !isFundAdequateForCustomer(fund, customer) : false;
                  if (!purchaseProcessLayout) {
                    return (
                        <RenderWhenVisible
                            key={fund.id}
                            offset={{ bottom: -300 }}
                            loader={<Skeleton className={c.skeletonListItem} variant="rect"/>}
                        >
                          <VisibilityWrapper setVisibility={(isVisible) => isVisible && onFundVisibility?.(fund, i)}>
                            <FundListItem
                                fund={fund}
                                isFundInadequate={isInadequate}
                                roi={rateKey}
                                unitCategoryCode={unitCategoryCode}
                                onSelectFund={onSelectFund}
                                hasMobilePricingDate={hasMobilePricingDate}
                                isSelected={selectedFundIds?.includes(fund.id)}
                                isButtonDisabled={disabled}
                                dynamicUnitCategoryCodeInDetails={dynamicUnitCategoryCodeInDetails}
                                fundDetailsDialogProcessLayout={fundDetailsDialogProcessLayout}
                                renderFundChart={renderFundChart}
                                renderFundDetailsDialog={renderFundDetailsDialog}
                                index={i}
                                autoTestId={generateAutoTestId?.(AutoTestIdPrefix.CARD, fund.name.toString())}
                            />
                          </VisibilityWrapper>
                        </RenderWhenVisible>);
                  } else {
                    return (
                        <RenderWhenVisible
                            key={fund.id}
                        offset={{ bottom: -300 }}
                        loader={<Skeleton className={c.skeletonListItem} variant="rect"/>}
                      >
                          <VisibilityWrapper setVisibility={(isVisible) => isVisible && onFundVisibility?.(fund, i)}>
                            <FundListItem
                                fund={fund}
                                fundValue={fund.amountOwned}
                                isFundInadequate={isInadequate}
                                roi={rateKey}
                                unitCategoryCode={unitCategoryCode}
                                onSelectFund={onSelectFund}
                                isSelected={fund.selected}
                                isButtonDisabled={disabled}
                                dynamicUnitCategoryCodeInDetails={dynamicUnitCategoryCodeInDetails}
                                fundDetailsDialogProcessLayout={fundDetailsDialogProcessLayout}
                                renderFundChart={renderFundChart}
                                renderFundDetailsDialog={renderFundDetailsDialog}
                                index={i}
                                autoTestId={generateAutoTestId?.(AutoTestIdPrefix.CARD, fund.name.toString())}
                            />
                          </VisibilityWrapper>
                      </RenderWhenVisible>)
                  }
                })
                : EmptyComponentRenderer
            )
            : <>
                <Skeleton className={c.skeletonListItem} variant="rect"/>
                <Skeleton className={c.skeletonListItem} variant="rect"/>
                <Skeleton className={c.skeletonListItem} variant="rect"/>
                <Skeleton className={c.skeletonListItem} variant="rect"/>
              </>}
          </div>
      );
    };

export default FullFundList;
